/**
 * The keyboard map, on `?`.
 *
 * Shortcuts that nobody can find are decoration. This is the one place they
 * are written down, and it opens from the same key every other developer tool
 * uses.
 */

import { navigation } from '../lib/navigation'
import { isConsole } from '../lib/surface'
import { useEffect, useRef, useState } from 'react'

const GROUPS: { title: string; keys: [string, string][] }[] = [
  {
    title: 'Anywhere',
    keys: [
      [/mac/i.test(navigator.platform) ? '⌘K' : 'Ctrl K', 'Search and run commands'],
      ['?', 'This list'],
      ['Esc', 'Close whatever is open'],
    ],
  },
  {
    title: 'Go to',
    keys: navigation(isConsole).filter(item => item.shortcut).map(item => [`g ${item.shortcut}`, item.label]),
  },
  {
    title: 'In the command palette',
    keys: [
      ['↑ ↓', 'Move through results'],
      ['↵', 'Run the highlighted one'],
      ['deploy…', 'Deploy, restart or suspend a service by name'],
    ],
  },
]

/** Dispatch this to open the sheet from anywhere. */
export const SHORTCUTS_EVENT = 'talaricloud:shortcuts'

export default function Shortcuts() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '?' || event.metaKey || event.ctrlKey) return
      // Typing a question mark into a field is not a request for help.
      const target = event.target as HTMLElement | null
      if (target?.isContentEditable) return
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return
      event.preventDefault()
      setOpen((current) => !current)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // The palette can open this too, so the shortcuts are findable by someone
  // who would never think to press "?".
  useEffect(() => {
    const open = () => setOpen(true)
    window.addEventListener(SHORTCUTS_EVENT, open)
    return () => window.removeEventListener(SHORTCUTS_EVENT, open)
  }, [])

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    <dialog
      className="palette"
      ref={ref}
      onCancel={() => setOpen(false)}
      onClose={() => setOpen(false)}
      aria-label="Keyboard shortcuts"
    >
      <div className="palette-box shortcuts">
        <h2 className="section-title" style={{ marginBottom: 4 }}>
          Keyboard
        </h2>
        {GROUPS.map((group) => (
          <section key={group.title}>
            <h3 className="tiny faint">{group.title}</h3>
            <dl>
              {group.keys.map(([key, description]) => (
                <div className="shortcut-row" key={key}>
                  <dt>
                    {key.split(' ').map((part) => (
                      <span className="kbd" key={part}>
                        {part}
                      </span>
                    ))}
                  </dt>
                  <dd className="tiny muted">{description}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </dialog>
  )
}

